import { useEffect, useState } from "react";
import { Loader2, Send, Mail, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  adminEmailAudience,
  adminEmailBlast,
  adminEmailBlasts,
  adminEmailBlast_get,
} from "../lib/api";

/**
 * AdminEmailBlastPanel — bulk-email all registered users from /admin.
 *
 * Writes the email in Markdown on the left, sees the live HTML preview
 * on the right (wrapped in a faux email card), confirms recipient count,
 * then hits Send. Each recipient gets their own personalised email so
 * nobody sees anyone else's address.
 *
 * Compliance footer (CASL):
 *   - Sender identity is added server-side
 *   - One-click unsubscribe link added server-side
 */
const AdminEmailBlastPanel = () => {
  const [audience, setAudience] = useState(null);
  const [loadingAudience, setLoadingAudience] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(
    "Hi there,\n\nA quick update from the Atomic Edu team —\n\n— David",
  );
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [currentBlast, setCurrentBlast] = useState(null);

  const loadAudience = async () => {
    setLoadingAudience(true);
    try {
      const a = await adminEmailAudience();
      setAudience(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load audience");
    } finally {
      setLoadingAudience(false);
    }
  };

  const loadHistory = async () => {
    try {
      const h = await adminEmailBlasts(10);
      setHistory(h.blasts || []);
    } catch {
      /* not critical */
    }
  };

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const [a, h] = await Promise.all([
          adminEmailAudience(),
          adminEmailBlasts(10).catch(() => ({ blasts: [] })),
        ]);
        if (!alive) return;
        setAudience(a);
        setHistory(h.blasts || []);
      } catch (e) {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Couldn't load audience");
      } finally {
        if (alive) setLoadingAudience(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, []);

  // Poll the just-sent blast until it flips to complete.
  useEffect(() => {
    if (!currentBlast || currentBlast.status !== "in_progress") return;
    const interval = setInterval(async () => {
      try {
        const b = await adminEmailBlast_get(currentBlast.id);
        setCurrentBlast(b);
        if (b.status !== "in_progress") {
          clearInterval(interval);
          loadHistory();
        }
      } catch {
        clearInterval(interval);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [currentBlast]);

  const canSend =
    subject.trim().length >= 2 &&
    body.trim().length >= 4 &&
    !sending &&
    (audience?.recipient_count || 0) > 0;

  const handleSend = async () => {
    setSending(true);
    setConfirmOpen(false);
    try {
      const res = await adminEmailBlast({
        subject: subject.trim(),
        body_markdown: body,
      });
      toast.success(`Queued for ${res.queued} teacher${res.queued === 1 ? "" : "s"}`);
      setCurrentBlast({
        id: res.blast_id,
        subject: subject.trim(),
        audience_size: res.queued,
        sent_count: 0,
        failed_count: 0,
        status: res.status,
      });
      setSubject("");
      setBody("Hi there,\n\n");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-blast-panel">
      {/* Audience banner */}
      <div className="ae-card p-5 md:p-6 flex items-start gap-4">
        <div className="rounded-lg bg-[#96ded1]/30 p-2.5">
          <Mail className="h-5 w-5 text-[#1c2d3d]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-extrabold text-base text-[#4a6984]">Audience</h3>
            <button
              type="button"
              onClick={loadAudience}
              disabled={loadingAudience}
              data-testid="admin-email-refresh-audience"
              className="text-xs text-[#4a6984]/70 hover:text-[#1c2d3d] inline-flex items-center gap-1"
            >
              <RefreshCw className={`h-3 w-3 ${loadingAudience ? "animate-spin" : ""}`} />
              refresh
            </button>
          </div>
          {loadingAudience ? (
            <p className="text-sm text-[#4a6984] mt-1">Loading audience size…</p>
          ) : audience ? (
            <p className="text-sm text-[#4a6984] mt-1 leading-relaxed">
              <span
                data-testid="admin-email-recipient-count"
                className="text-[#1c2d3d] font-extrabold text-lg"
              >
                {audience.recipient_count}
              </span>{" "}
              registered teacher{audience.recipient_count === 1 ? "" : "s"} will
              receive this email.{" "}
              {audience.opted_out_count > 0 && (
                <span className="text-[#4a6984]/70">
                  ({audience.opted_out_count} unsubscribed and will be skipped.)
                </span>
              )}
              {!audience.configured && (
                <span className="block mt-2 text-rose-700 font-bold">
                  ⚠ Resend is not configured. Set RESEND_API_KEY and
                  RESEND_FROM_EMAIL in backend .env before sending.
                </span>
              )}
            </p>
          ) : null}
        </div>
      </div>

      {/* Live status of the most recent blast */}
      {currentBlast && (
        <div
          data-testid="admin-email-current-status"
          className={`ae-card p-5 md:p-6 border-l-4 ${
            currentBlast.status === "complete"
              ? "border-l-emerald-500"
              : "border-l-amber-500"
          }`}
        >
          <div className="flex items-center gap-3">
            {currentBlast.status === "complete" ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            ) : (
              <Loader2 className="h-5 w-5 text-amber-600 animate-spin shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-sm text-[#1c2d3d] truncate">
                {currentBlast.subject}
              </p>
              <p className="text-xs text-[#4a6984] mt-0.5">
                {currentBlast.status === "complete"
                  ? `Sent to ${currentBlast.sent_count} / ${currentBlast.audience_size}` +
                    (currentBlast.failed_count
                      ? ` · ${currentBlast.failed_count} failed`
                      : "")
                  : `Sending… ${currentBlast.sent_count || 0} / ${currentBlast.audience_size}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Compose + preview */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Compose */}
        <div className="ae-card p-5 md:p-6 space-y-4">
          <h3 className="font-extrabold text-base text-[#4a6984] tracking-tight">
            Compose
          </h3>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4a6984]/70 block mb-1.5">
              Subject
            </label>
            <input
              type="text"
              data-testid="admin-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              placeholder="A quick update from Atomic Edu"
              className="w-full px-3 py-2 rounded-md border border-[#4a6984]/20 bg-white text-[#1c2d3d] text-sm focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
            />
            <p className="text-[10px] text-[#4a6984]/60 mt-1">
              {subject.length}/200
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4a6984]/70 block mb-1.5">
              Body (Markdown)
            </label>
            <textarea
              data-testid="admin-email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={20000}
              rows={14}
              placeholder="Hi there,\n\nWrite your message using **Markdown** — bold, *italic*, [links](https://atomicedu.ca), and lists all work."
              className="w-full px-3 py-2 rounded-md border border-[#4a6984]/20 bg-white text-[#1c2d3d] text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#96ded1] resize-y"
            />
            <p className="text-[10px] text-[#4a6984]/60 mt-1">
              {body.length}/20000 · Supports{" "}
              <code className="text-[#1c2d3d]">**bold**</code>,{" "}
              <code className="text-[#1c2d3d]">[link](url)</code>, lists, headings
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={!canSend}
            data-testid="admin-email-send-btn"
            className="w-full bg-[#1c2d3d] hover:bg-[#0f1e2e] text-white font-extrabold inline-flex items-center justify-center gap-2"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send to {audience?.recipient_count || 0} teacher
            {audience?.recipient_count === 1 ? "" : "s"}
          </Button>
        </div>

        {/* Preview */}
        <div className="ae-card p-5 md:p-6 bg-[#f4f5f7]/70">
          <h3 className="font-extrabold text-base text-[#4a6984] tracking-tight mb-4">
            Preview
          </h3>
          <div className="bg-white rounded-lg overflow-hidden border border-[#4a6984]/10">
            <div className="bg-[#1c2d3d] px-5 py-4">
              <p className="text-white font-extrabold text-sm">Atomic Edu</p>
            </div>
            <div className="px-5 py-5">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#4a6984]/60 mb-1">
                Subject
              </p>
              <p
                data-testid="admin-email-preview-subject"
                className="font-extrabold text-base text-[#1c2d3d] mb-4"
              >
                {subject || "(no subject yet)"}
              </p>
              <div
                data-testid="admin-email-preview-body"
                className="ae-prose text-sm"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {body || "_Start writing in Markdown…_"}
                </ReactMarkdown>
              </div>
              <div className="mt-6 pt-4 border-t border-[#4a6984]/12 text-[11px] text-[#4a6984]/70 leading-relaxed">
                You&rsquo;re receiving this because you have an Atomic Edu account.
                <br />
                Atomic Edu · Ontario, Canada · info@atomicedu.ca
                <br />
                <span className="underline">Unsubscribe from these emails</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent blasts history */}
      {history.length > 0 && (
        <div className="ae-card p-5 md:p-6">
          <h3 className="font-extrabold text-base text-[#4a6984] tracking-tight mb-3">
            Recent blasts
          </h3>
          <div className="space-y-2">
            {history.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-email-history-${b.id}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[#f4f5f7] text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[#1c2d3d] truncate">
                    {b.subject}
                  </p>
                  <p className="text-xs text-[#4a6984]">
                    {new Date(b.started_at).toLocaleString()}
                  </p>
                </div>
                <div className="text-xs text-[#4a6984] shrink-0">
                  {b.status === "complete" ? (
                    <span>
                      ✓ {b.sent_count} sent
                      {b.failed_count > 0 && ` · ${b.failed_count} failed`}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> sending…
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmOpen && (
        <div
          data-testid="admin-email-confirm"
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight">
                  Send to {audience?.recipient_count || 0} teachers?
                </h3>
                <p className="text-sm text-[#4a6984] mt-1">
                  This will deliver &ldquo;{subject || "(no subject)"}&rdquo; to every
                  registered Atomic Edu user. You can&rsquo;t unsend.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                data-testid="admin-email-confirm-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSend}
                data-testid="admin-email-confirm-send"
                className="bg-[#1c2d3d] hover:bg-[#0f1e2e] text-white font-extrabold"
              >
                <Send className="h-4 w-4 mr-1.5" />
                Yes, send to {audience?.recipient_count || 0}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailBlastPanel;
